import { forEach, filter as _filter, assign, uniq, sortBy } from 'lodash';
import { select, scaleLinear, buffer as d3buffer, json as d3json, nest } from 'd3c';
import { d3TilesVector } from '../libs/d3-tile-vector';
import { default as vectorTile } from '../libs/mapbox_vectortile';
import { default as Protobuf } from 'pbf';

let parsedStyles = {};
let labelsPool = {}; // pool of labels for Vector tiles

export default class GeomapVectorTiles {
	constructor(options) {
		this.options = options;
		this._state = {
			vectorTilesStyleParsed: false,
			tileVector: null, // determines which tiles to fetch
			prevTilesZoom: -1,
			lastZoomTransform: null, // Maps path accord. to projection
			zoomScale: 1,
			zoomScaleChanged: true
		};
		this.svgContainer = select(this.options.element).select('svg');
		this.svgContainer.insert('svg:g', ':first-child').attr('class', 'datamaps-labellayer');
		this.svgContainer.insert('svg:g', ':first-child').attr('class', 'datamaps-baselayer');
		this.svgContainer
			.insert('svg:rect', ':first-child')
			.attr('class', 'datamaps-bg')
			.attr('width', this.options.element.offsetWidth)
			.attr('height', this.options.element.offsetHeight)
			.style('fill', 'rgb(242, 242, 242)'); // @portal-color-gray-95
	}

	// returns Promise
	init() {
		this._state.tileVector = d3TilesVector()
			.size([this.options.element.offsetWidth, this.options.element.offsetHeight])
			.wrap(false);
		this.options.tilesConfig.showContributors && this.appendContributors();
		return this.parseVectorStyles(this.options.tilesConfig.stylesUrl);
	}

	zoomed(mapState, currentZoomTransform, isZoomChanged, errorCallback) {
		this._state.zoomScaleChanged = isZoomChanged;
		this._state.zoomScale = currentZoomTransform.k;
		this.svgContainer.select('.datamaps-baselayer').attr('transform', currentZoomTransform);
		this.svgContainer
			.select('.datamaps-labellayer')
			.attr('transform', `translate(${currentZoomTransform.x},${currentZoomTransform.y})`);
		this.updateTiles(
			mapState,
			[currentZoomTransform.x, currentZoomTransform.y],
			currentZoomTransform.k * 128,
			errorCallback
		);
		this.updateTileLabels(mapState, currentZoomTransform);
	}

	updateTiles(mapState, t, s, errorCallback) {
		const self = this;
		if (this.options.element.offsetWidth === 0 || this.options.element.offsetHeight === 0) {
			// Map is not showed
			return;
		}
		// Defines how much labels should be taken from layer (%) depending on tilesZoom
		const labelsCutOff = scaleLinear()
			.domain([0, 2, 3, 8, 10, 13])
			.range([0, 0.25, 0.15, 0.85, 0.6, 0.5]);

		if (this._state.vectorTilesStyleParsed) {
			drawVectorTiles.call(this, t, s);
		} else {
			setTimeout(() => {
				this.updateTiles(mapState, t, s, errorCallback);
			}, 500);
		}

		function drawVectorTiles(t, s) {
			const config = this.options.tilesConfig;
			if (config.url == null) {
				return;
			}
			const tilesContainer = this.svgContainer.select('g.datamaps-baselayer');

			this._state.tileVector
				.scale(s)
				.translate([
					t[0] - this.options.element.offsetWidth / (2 * s),
					t[1] - this.options.element.offsetHeight / (2 * s)
				]);

			const tilesToFetch = this._state.tileVector();
			let tilesCount = tilesToFetch.length;
			if (tilesCount === 0) {
				return;
			}
			let requestErrorFlag = false;
			if (this._state.prevTilesZoom !== tilesToFetch[0].z) {
				// tilesZoom changes
				this._state.prevTilesZoom = tilesToFetch[0].z;
				tilesContainer
					.selectAll('.vector-tile')
					.classed('old-tile', true)
					.classed('vector-tile', false);
			}

			const updTiles = tilesContainer
				.style('fill', 'none')
				.selectAll('.vector-tile')
				.data(tilesToFetch, function(d) {
					return `${d.z}-${d.x}-${d.y}`;
				});

			updTiles
				.enter()
				.append('g')
				.attr('class', 'vector-tile')
				.attr('data-tile', function(d) {
					return `${d.z}-${d.x}-${d.y}`;
				})
				.each(function(tile) {
					const selfTile = this;
					d3buffer(`${config.url}${tile.z}/${tile.x}/${tile.y}${config.extension}`)
						.then(function(response) {
							// late fetch
							if (self._state.prevTilesZoom !== tile.z) {
								return;
							}
							tile.layers = new vectorTile.VectorTile(new Protobuf(response)).layers;
							if (Object.keys(tile.layers).length === 0) {
								throw new Error('Empty tile recieved');
							}
							forEach(tile.layers, layer => {
								if (!parsedStyles[layer.name] && layer.name !== 'place') {
									// Layer has no styles - no need to be rendered
									delete tile.layers[layer.name];
								}
							});

							// Workaround with layer order (after converting from .pbf order is not correct)
							const layerOrder = [
								'park',
								'landuse',
								'landcover',
								'building',
								'water',
								'waterway',
								'aeroway',
								'transportation',
								'boundary',
								'place'
							];
							const tileLayers = Object.keys(tile.layers);
							const layers = [];
							for (let i = 0; i < layerOrder.length; i++) {
								if (tileLayers.indexOf(layerOrder[i]) > -1) {
									layers.push(layerOrder[i]);
								}
							}
							if (layers.indexOf('park') > -1) {
								layers.splice(layers.indexOf('park'), 1);
								layers.unshift('park');
							}

							select(selfTile)
								.selectAll('g')
								.data(layers)
								.enter()
								.append('g')
								.attr('class', function(d) {
									return d;
								})
								.each(function(layerName) {
									let geoJson = geojson(tile, tile.layers[layerName]).features;
									const d3layer = select(this);

									// Add labels to pool
									if (layerName === 'place') {
										if (tile.z < config.minZoomScaleForCountryLabels) {
											return;
										}
										addTileLabels(geoJson, tile.z);
										return;
									}

									// Avoid rendering objects without any styles, svg Points
									geoJson = _filter(geoJson, elem => {
										elem.styles = findStyleRule(elem, layerName, tile.z);
										return elem.styles && elem.geometry.type !== 'Point';
									});

									d3layer
										.selectAll('path')
										.data(geoJson)
										.enter()
										.append('path')
										.attr('d', function(d) {
											return mapState.path(d);
										})
										.each(function(d) {
											const d3path = select(this);
											if (d.properties.admin_level) {
												//  Dont render marine borders
												if (d.properties.maritime === 1) {
													return;
												}
												if (d.properties.admin_level === 4) {
													d3path.attr('stroke-dasharray', `${2 / self._state.zoomScale},${2 / self._state.zoomScale}`);
												}
											}
											d3path
												.attr('fill', d.styles['fill-color'] ? d.styles['fill-color'] : 'none')
												.attr('fill-opacity', function() {
													if (d.styles['fill-opacity'] && typeof d.styles['fill-opacity'] == 'number') {
														return d.styles['fill-opacity'];
													} else {
														return '1';
													}
												})
												// .attr('class', d.properties.class ? d.properties.class : d.properties.subclass) // for debugging
												.attr('stroke', d.styles.stroke || d.styles['line-color']);
										});
								});
							tilesCount--;
							// Last tile in pack, prev tileLayer to clean
							if (tilesCount === 0) {
								tilesContainer
									.selectAll('.old-tile')
									.transition()
									.duration(500)
									.style('opacity', 0)
									.remove();
								if (self._state.prevTilesZoom === tile.z) {
									// Draw labels for maximal possible zoom
									drawTileLabels(config, tile.z);
								}
							}
						})
						.catch(() => {
							// Check if previous zoom level tiles are rendered
							if (
								!requestErrorFlag &&
								parseInt(tilesContainer.select('.old-tile').attr('data-tile').split('-')[0], 10) < self._state.prevTilesZoom - 4
							) {
								requestErrorFlag = true;
								errorCallback(t, s);
							}
							return true;
						});
				});

			this.svgContainer.select('.datamaps-baselayer').attr('stroke-width', 1 / this._state.zoomScale);
			this._state.zoomScaleChanged = false;
			// If some labels were cached for needed zoomLevel
			if (labelsPool[this._state.prevTilesZoom] && labelsPool[this._state.prevTilesZoom].length >= 0) {
				drawTileLabels(config, this._state.prevTilesZoom);
			}
		}

		// Finds a style rule for geojson element. Takes first match.
		function findStyleRule(element, layerName, tilesZoom) {
			for (let i = 0; i < parsedStyles[layerName].length; i++) {
				const rule = parsedStyles[layerName][i];
				// Check zoom condition
				if ((rule.minzoom && rule.minzoom > tilesZoom) || (rule.maxzoom && rule.maxzoom < tilesZoom)) {
					continue;
				}
				if (!rule.filter || checkFilter(element, rule.filter)) {
					return rule.paint;
				}
			}
			return null;
		}

		function checkFilter(element, filter) {
			if (filter[0] === '==') {
				const comaparedValue = getComparedValue(filter[1]);
				return comaparedValue === filter[2] || comaparedValue === `Multi${filter[2]}`; // Workaround with MultiLineString and MultiPolygon
			} else if (filter[0] === '!=') {
				return getComparedValue(filter[1]) !== filter[2];
			} else if (filter[0] === '<=') {
				return getComparedValue(filter[1]) <= filter[2];
			} else if (filter[0] === 'in') {
				return filter.slice(2).indexOf(getComparedValue(filter[1])) !== -1;
			} else if (filter[0] === '!in') {
				return filter.slice(2).indexOf(getComparedValue(filter[1])) === -1;
			} else if (filter[0] === 'all') {
				const filters = filter.slice(1);
				for (let i = 0; i < filters.length; i++) {
					if (!checkFilter(element, filters[i])) {
						return false;
					}
				}
				return true;
			} else {
				return false;
			}

			function getComparedValue(field) {
				let comparedValue;
				if (field === '$type') {
					comparedValue = element.geometry.type;
				} else {
					comparedValue = element.properties[field];
				}
				return comparedValue;
			}
		}

		function addTileLabels(geoJson, tilesZoom) {
			labelsPool[tilesZoom] || (labelsPool[tilesZoom] = []);
			labelsPool[tilesZoom] = labelsPool[tilesZoom].concat(
				// Assumption, that labels coming in importance order
				geoJson
					.filter(el => el.properties.class !== 'continent' && (tilesZoom < 4 ? el.properties.class !== 'state' : true))
					.slice(0, geoJson.length * labelsCutOff(tilesZoom))
					.map(el =>
						assign(
							{},
							{
								id: el.id,
								coordinates: el.geometry.coordinates,
								class: el.properties.class,
								'name:en': el.properties['name:en'],
								'name:de': el.properties['name:de'],
								'name:fr': el.properties['name:fr'],
								'name:es': el.properties['name:es']
							}
						)
					)
			);
			labelsPool[tilesZoom] = uniq(labelsPool[tilesZoom], el => el.id);
		}

		function drawTileLabels(config, tilesZoom) {
			labelsPool = forEach(labelsPool, (v, k) => {
				if (k > tilesZoom) {
					delete labelsPool[k];
				}
			});
			const labels = select('.datamaps-labellayer')
				.selectAll('.datamaps-label-country')
				.data(sortBy(labelsPool[tilesZoom], el => el.id), e => e.id);

			labels.exit().remove();

			labels
				.enter()
				.append('text')
				.attr('class', 'datamaps-label-country')
				.text(function(d) {
					return d[`name:${config.language}`];
				})
				.attr('x', function(d) {
					return mapState.projectionForLabels(d.coordinates)[0];
				})
				.attr('y', function(d) {
					return mapState.projectionForLabels(d.coordinates)[1];
				})
				.attr('text-anchor', 'middle')
				.style('font-weight', 900)
				.style('fill', function(o) {
					return o.class === 'country' ? config.countryLabelColor : config.cityLabelColor;
				})
				.style('stroke', '#ffffff') // @portal-color-white
				.style('stroke-width', function(o) {
					return o.class === 'country' ? '0.5px' : '0.3px';
				})
				.merge(labels)
				.style('font-size', function(o) {
					let labelSize;
					switch (o.class) {
						case 'country':
							labelSize = config.countryLabelFontSizeInterpolate(tilesZoom);
							break;
						case 'city':
							labelSize = config.cityLabelFontSizeInterpolate(tilesZoom);
							break;
						default:
							labelSize = config.otherLabelFontSizeInterpolate(tilesZoom);
							break;
					}
					return `${labelSize}px`;
				});
			self.updateTileLabels(mapState);
		}
	}

	updateTileLabels(mapState, currentZoomTransform) {
		if (!currentZoomTransform) {
			currentZoomTransform = this._state.lastZoomTransform;
		}
		this._state.lastZoomTransform = currentZoomTransform;
		mapState.projectionForLabels.scale((currentZoomTransform.k * 128) / 2 / Math.PI);
		this.svgContainer
			.selectAll('.datamaps-label-country')
			.attr('x', function(d) {
				return mapState.projectionForLabels(d.coordinates)[0];
			})
			.attr('y', function(d) {
				return mapState.projectionForLabels(d.coordinates)[1];
			});
	}

	updateContainerSize() {
		this._state.tileVector.size([this.options.element.offsetWidth, this.options.element.offsetHeight]);
		this.options.tilesConfig.showContributors && this.appendContributors();
		this.svgContainer
			.select('.datamaps-bg')
			.attr('width', this.options.element.offsetWidth)
			.attr('height', this.options.element.offsetHeight);
	}

	parseVectorStyles(styleUrl) {
		return d3json(styleUrl).then(
			function(json) {
				parseStylesJSON(json, this.options);
				this.svgContainer.select('svg .datamaps-bg').attr('fill', parsedStyles.background[0].paint['background-color']);
				this._state.vectorTilesStyleParsed = true;
				return 1;
			}.bind(this)
		);

		function parseStylesJSON(stylesJSON, options) {
			const filteredStyles = [];

			stylesJSON.layers.forEach(function(layer) {
				if (layer.id === 'place_label_city') {
					options.tilesConfig.cityLabelFontSizeInterpolate = createFontSizeScale(layer);
					return;
				}
				if (layer.id === 'country_label') {
					options.tilesConfig.countryLabelFontSizeInterpolate = createFontSizeScale(layer);
					return;
				}
				if (layer.id === 'place_label_other') {
					options.tilesConfig.otherLabelFontSizeInterpolate = createFontSizeScale(layer);
					return;
				}
				if (layer.type !== 'symbol') {
					filteredStyles.push(layer);
				}
			});

			parsedStyles = nest()
				.key(d => d['source-layer'])
				.object(filteredStyles);

			parsedStyles.background = parsedStyles.undefined;
			delete parsedStyles.undefined;
		}

		function createFontSizeScale(layer) {
			const interpolatorFactor = 0.5; // Found empirically
			const interpolator = scaleLinear();
			if (layer.layout && layer.layout['text-size'] && layer.layout['text-size'].stops) {
				const stops = layer.layout['text-size'].stops;
				const domain = [];
				const range = [];
				stops.forEach(function(stop, i) {
					if (stop[1] === 0) {
						domain.push(stop[0] * (stop[1] === 0 ? interpolatorFactor : 1));
						range.push(stops[i + 1] ? stops[i + 1][1] : stop[1]);
					} else {
						domain.push(stop[0]);
						range.push(stop[1]);
					}
				});
				interpolator.domain(domain).range(range);
			} else {
				interpolator.domain([0, layer.maxZoom]).range(0, 12);
			}
			return interpolator;
		}
	}

	appendContributors() {
		this.svgContainer.selectAll('.geomap-contributors').remove();

		const transform = `translate(${[5, this.options.element.offsetHeight - 9]})`;

		['text-background', 'text-foreground'].forEach(className => {
			// only show on hover if below a certain size
			const contributors = this.svgContainer
				.append('text')
				.attr('class', 'geomap-contributors')
				.attr('transform', transform)
				.classed('hidden', true)
				.classed(className, true);

			contributors.append('svg:tspan').text('© ');

			contributors
				.append('svg:tspan')
				.append('svg:a')
				.attr('xlink:href', 'https://openmaptiles.org/')
				.attr('target', '_blank')
				.text('OpenMapTiles');

			contributors.append('svg:tspan').text(' | ');

			contributors
				.append('svg:tspan')
				.append('svg:a')
				.attr('xlink:href', 'https://openstreetmap.org/about/')
				.attr('target', '_blank')
				.text('OpenStreetMap');
		});
	}
}

// Converts layerData to geojson
function geojson({ x, y, z }, layer, filter = () => true) {
	if (!layer) {
		return;
	}
	const features = [];
	for (let i = 0; i < layer.length; ++i) {
		const f = layer.feature(i).toGeoJSON(x, y, z);
		if (filter.call(null, f, i, features)) {
			features.push(f);
		}
	}
	return { type: 'FeatureCollection', features };
}
